import { z } from 'zod';
import { PersonRef } from './people.js';
/**
 * Break-glass support access (docs/adr/0051-break-glass-support-access.md; brief: Multi-tenancy). Chapsi staff ask
 * from the platform admin for read-only access to named areas of one workspace, for at most four hours; a workspace
 * owner approves or denies it in Workspace settings > Security > Support access. Every view under a grant is written
 * to the workspace's audit log and the platform staff log.
 */
/** Longest window an owner can approve, and the default staff ask for. */
export const SUPPORT_ACCESS_MAX_MINUTES = 240;
export const SUPPORT_ACCESS_DEFAULT_MINUTES = 60;
export const SUPPORT_ACCESS_DURATIONS = [15, 30, 60, 120, 180, 240];
/** An unanswered request lapses after this long. */
export const SUPPORT_ACCESS_REQUEST_HOURS = 24;
export const SUPPORT_ACCESS_MAX_CHANNELS = 5;
export const SUPPORT_ACCESS_MAX_TASKS = 10;
/**
 * What a grant opens. `mode` is always read in v1: there is no way to write under a grant. `settings` covers the
 * workspace's settings, feature switches and statuses; channels and tasks are named one by one (never a DM).
 */
export const SupportAccessScope = z
    .object({
    mode: z.literal('read'),
    settings: z.boolean(),
    channelIds: z.array(z.uuid()).max(SUPPORT_ACCESS_MAX_CHANNELS),
    workItemIds: z.array(z.uuid()).max(SUPPORT_ACCESS_MAX_TASKS),
})
    .strict()
    .refine((s) => s.settings || s.channelIds.length > 0 || s.workItemIds.length > 0, { message: 'Choose at least one area' })
    .refine((s) => new Set(s.channelIds).size === s.channelIds.length && new Set(s.workItemIds).size === s.workItemIds.length, { message: 'Each area once' });
/**
 * pending: waiting for an owner. approved: the grant is open now. denied, lapsed (unanswered in 24 hours) and
 * cancelled (withdrawn by staff) never opened. expired, revoked (by an owner) and ended (by staff, or the workspace
 * switched support access off) were open and are closed.
 */
export const SupportAccessStatus = z.enum(['pending', 'approved', 'denied', 'lapsed', 'cancelled', 'expired', 'revoked', 'ended']);
export const SupportAccessEndReason = z.enum(['expired', 'revoked', 'ended_by_staff', 'policy_off', 'request_lapsed', 'cancelled']);
/** "Never allow support access" (off) or "Allow when an owner approves" (the default except on Enterprise). */
export const SupportAccessPolicy = z.enum(['on_request', 'never']);
/** The plan's default when the workspace has not chosen: Enterprise starts with support access off. */
export function supportAccessPlanDefault(plan) {
    return plan === 'enterprise' ? 'never' : 'on_request';
}
const iso = z.string();
// --- In the app (owners) --------------------------------------------------------------------------------
/** An approved area as the owner sees it, by name. `visible` is false for something the owner can't open. */
export const SupportAccessArea = z.object({
    kind: z.enum(['settings', 'channel', 'task']),
    id: z.uuid().nullable(),
    label: z.string(),
    /** False when the owner can't approve it: a DM, a private channel they are not in, or something that no longer exists. */
    approvable: z.boolean(),
});
export const SupportAccessRequest = z.object({
    id: z.uuid(),
    staffEmail: z.string(),
    reason: z.string(),
    ticketRef: z.string(),
    durationMinutes: z.number().int(),
    status: SupportAccessStatus,
    areas: z.array(SupportAccessArea),
    requestedAt: iso,
    requestExpiresAt: iso,
    decidedBy: PersonRef.nullable(),
    decidedAt: iso.nullable(),
    decisionNote: z.string().nullable(),
    startsAt: iso.nullable(),
    expiresAt: iso.nullable(),
    endedAt: iso.nullable(),
    endReason: SupportAccessEndReason.nullable(),
    endedBy: PersonRef.nullable(),
});
export const SupportAccessOverview = z.object({
    policy: SupportAccessPolicy,
    /** What the plan would choose; `custom` when an owner picked the policy themselves. */
    planDefault: SupportAccessPolicy,
    custom: z.boolean(),
    /** Newest first: open requests and grants, then the last 50 closed ones. */
    requests: z.array(SupportAccessRequest),
});
export const UpdateSupportAccessPolicyInput = z.object({ policy: SupportAccessPolicy }).strict();
const Note = z.string().trim().max(500);
/** Approving needs a fresh step-up code (purpose `support_access`), like exports and deletion. */
export const ApproveSupportAccessInput = z.object({ code: z.string().regex(/^\d{6}$/, 'Enter the six-digit code'), note: Note.optional() }).strict();
export const DenySupportAccessInput = z.object({ note: Note.optional() }).strict();
// --- In the platform admin (staff) ----------------------------------------------------------------------
export const CreateSupportAccessInput = z
    .object({
    reason: z.string().trim().min(10, 'Say why in at least 10 characters').max(500),
    ticketRef: z.string().trim().min(1, 'Add the support ticket reference').max(80),
    scope: SupportAccessScope,
    durationMinutes: z.number().int().refine((n) => SUPPORT_ACCESS_DURATIONS.includes(n), { message: 'Choose a duration up to 4 hours' }),
})
    .strict();
export const StaffSupportAccess = z.object({
    id: z.uuid(),
    tenantId: z.uuid(),
    tenantName: z.string(),
    staffEmail: z.string(),
    reason: z.string(),
    ticketRef: z.string(),
    scope: z.object({ mode: z.literal('read'), settings: z.boolean(), channelIds: z.array(z.uuid()), workItemIds: z.array(z.uuid()) }),
    durationMinutes: z.number().int(),
    status: SupportAccessStatus,
    requestedAt: iso,
    requestExpiresAt: iso,
    decidedAt: iso.nullable(),
    decisionNote: z.string().nullable(),
    startsAt: iso.nullable(),
    expiresAt: iso.nullable(),
    endedAt: iso.nullable(),
    endReason: SupportAccessEndReason.nullable(),
});
export const SupportAccessListQuery = z.object({ tenantId: z.uuid().optional() });
/** The read-only support view's front page: what the grant opens and until when. */
export const SupportViewOverview = z.object({
    requestId: z.uuid(),
    tenantId: z.uuid(),
    tenantName: z.string(),
    startsAt: iso,
    expiresAt: iso,
    settings: z.boolean(),
    channels: z.array(z.object({ id: z.uuid(), name: z.string(), private: z.boolean(), archived: z.boolean() })),
    tasks: z.array(z.object({ id: z.uuid(), number: z.number().int(), type: z.string(), title: z.string(), projectKey: z.string().nullable() })),
});
export const SupportViewMessage = z.object({
    id: z.uuid(),
    seq: z.number().int(),
    author: z.string().nullable(),
    body: z.string().nullable(),
    deleted: z.boolean(),
    edited: z.boolean(),
    isReply: z.boolean(),
    createdAt: iso,
});
export const SupportViewChannel = z.object({ channelId: z.uuid(), name: z.string(), items: z.array(SupportViewMessage) });
export const SupportViewChannelQuery = z.object({ beforeSeq: z.coerce.number().int().positive().optional() });
export const SupportViewTask = z.object({
    id: z.uuid(),
    number: z.number().int(),
    type: z.string(),
    title: z.string(),
    description: z.string().nullable(),
    status: z.string().nullable(),
    priority: z.string().nullable(),
    assignee: z.string().nullable(),
    dueDate: z.string().nullable(),
    estimateMinutes: z.number().int().nullable(),
    project: z.string().nullable(),
    projectKey: z.string().nullable(),
    createdAt: iso,
    updatedAt: iso,
    completedAt: iso.nullable(),
    subtasks: z.array(z.object({ title: z.string(), done: z.boolean() })),
    comments: z.array(z.object({ author: z.string().nullable(), body: z.string().nullable(), deleted: z.boolean(), createdAt: iso })),
});
export const SupportViewSettings = z.object({
    features: z.record(z.string(), z.boolean()),
    settings: z.record(z.string(), z.unknown()),
    statuses: z.array(z.object({ label: z.string(), category: z.string() })),
});
//# sourceMappingURL=support-access.js.map